type PortAddressSensor = `${"S"}${number}`;
type PortAddressIntegrated = `${"I"}${number}`;
type PortAddressDiagnostic = `${"D"}${number}`;
type PortAddressValve = `${"V"}${number}`;
/**
 * S = Sensor (external, user-configurable)
 * I = Integrated sensor (fixed, built into the device)
 * D = Diagnostic (firmware-generated internal metrics)
 * V = Valve
 */
type PortAddress = PortAddressSensor | PortAddressIntegrated | PortAddressDiagnostic | PortAddressValve;

export enum SensorPortCommunicationMode {
    DIRECT = "DIRECT",
    /** Firmware-internal / software-computed data — no physical wiring. Used for virtual, diagnostic, and internal-sensor ports. */
    DIGITAL = "DIGITAL",
    SDI12 = "SDI12",
    RS485 = "RS485",
}

/**
 * Persisted sub-channel address. Stored on the wire and in the database as a plain
 * non-negative integer. The meaning of each value depends on the communication mode:
 *
 *   DIRECT  — see {@link DirectSubChannel} for the canonical map.
 *   SDI-12  — no sub-channel map; SDI-12 sensors are addressed by channel number alone.
 *   RS-485  — no sub-channel map; RS-485 sensors are addressed by channel number alone.
 *
 * Use the mode-specific constant map (e.g. `DirectSubChannel.PULSE_FREQUENCY`) when
 * assigning values; the bare `number` type is exposed because the persisted form is
 * mode-agnostic.
 */
export type SubChannel = number;

/**
 * DIRECT-mode sub-channel map. Identifies the specific measurement source within a
 * DIRECT-addressed channel.
 *
 *   PULSE_FREQUENCY — PWM / pulse-frequency input (flowmeters, encoders): instantaneous Hz
 *   TOTAL           — cumulative / totalized pulse count (flow volume, pulse totals)
 *   ADC_5V          — 5 V analog input (pressure transducers, voltage sensors)
 *   ADC_12V         — 12 V analog input (boost-converter-powered sensors on Gen4 S1)
 *
 * The numeric values are part of the persisted sensor address. Do not renumber or
 * reuse an existing entry — only append new entries at the end. SDI-12 and RS-485
 * do not currently have sub-channel maps; their sub-channel field is left unset.
 */
export const DirectSubChannel = {
    PULSE_FREQUENCY: 0,
    TOTAL: 1,
    ADC_5V: 2,
    ADC_12V: 3,
} as const;
export type DirectSubChannel = (typeof DirectSubChannel)[keyof typeof DirectSubChannel];

export type ChannelModeConfig = {
    isAvailable: boolean;
    availableChannelRange: [number, number];
    maxActiveChannels: number;
    supportedSubChannels: SubChannel[];
};

export const unavailableChannelMode: ChannelModeConfig = {
    isAvailable: false,
    availableChannelRange: [0, 0],
    maxActiveChannels: 0,
    supportedSubChannels: [],
};

export function directChannelMode(supportedSubChannels: DirectSubChannel[]): ChannelModeConfig {
    return {
        isAvailable: true,
        availableChannelRange: [0, 0],
        maxActiveChannels: 1,
        supportedSubChannels,
    };
}

export function digitalChannelMode(): ChannelModeConfig {
    return {
        isAvailable: true,
        availableChannelRange: [0, 0],
        maxActiveChannels: 1,
        supportedSubChannels: [],
    };
}

export function sdi12ChannelMode(maxChannels = 10): ChannelModeConfig {
    return {
        isAvailable: true,
        availableChannelRange: [0, maxChannels - 1],
        maxActiveChannels: maxChannels,
        supportedSubChannels: [],
    };
}

export type PortBase<Address extends PortAddress = PortAddress> = {
    isFixed: boolean;
    portId: Address;
    channelRangeByCommunicationMode: Record<SensorPortCommunicationMode, ChannelModeConfig>;
};

export type FixedSensorPort<Address extends PortAddress = PortAddress> = PortBase<Address> & {
    isFixed: true;
    /**
     * Indicates that this does not correspond with physical hardware that has been deployed.
     * Physical sensors have model names and manufacturers and directly measure physical phenomenon. Examples include:
     *  - A temperature sensor
     *  - A humidity sensor
     *  - A soil moisture sensor
     *  - A battery level sensor
     *
     * Virtual sensors are sensors that only exist in software and do not directly measure physical phenomenon. Examples include:
     *  - A reset counter
     *  - A network statistics sensor
     *  - A sensor indicating what percentage of valves are open in a zone
     */
    isVirtual: boolean;

    /**
     * Indicates that this sensor is entirely within the device and not exposed to the outside. This does not
     * include Senosterra sensor probes, or Dragino EC sensors, since even though they are *integrated*, they are not *internal*
     */
    isInternal: boolean;

    // commented out for now, maybe not necessary
    // /**
    //  * Should users be able to see / configure this sensor? If true, it will show up in the device config section of
    //  * the device card.
    //  */
    // configExposedToUsers: boolean;
    //
    // /**
    //  * Should users be able to see data collected by this sensor?
    //  */
    // valuesExposedToUsers: boolean;

    /**
     * The identifier for the sensor cluster that must be attached to this sensor port
     */
    sensorClusterToPopulateWith: string;
};

export type OpenSensorPort<Address extends PortAddress = PortAddress> = PortBase<Address> & {
    isFixed: false;
};

export type DevicePortConfig = Partial<{ [K in PortAddress]: FixedSensorPort<K> | OpenSensorPort<K> }>;
